import { useRef, useState } from "react";
import { listAccountConfigs } from "../api/config";
import { importSubscriptionCsv } from "../api/consistency";
import type { ImportSubscriptionCsvResultDto } from "../api/types";
import { useAsync } from "../hooks/useAsync";

/**
 * §7.4 导入订阅状态 CSV 核对台账(新增页面,仅 Admin 可见入口)。
 *
 * 背景:Kiro 无公开订阅状态 API(C2),平台台账的 subscription_status 只会
 * 被"退订/到期回收/一致性核对"这三处代码路径改成 CANCELED,从来没有路径
 * 会把它改成 ACTIVE——导致看板"激活率"长期 0%、§7.3 到期回收永远匹配不到
 * 任何 ACTIVE 用户。这个页面把管理员从 Kiro 控制台"Users & Groups"页面
 * 手动下载的订阅 CSV(Download CSV 按钮)导入进来,后端按邮箱匹配台账、
 * 用 CSV 的真实状态覆盖台账状态,是"半自动化"方案——下载这一步仍需人工
 * (Kiro 没有开放生成 CSV 的自动化入口),下载完之后的比对/更新/审计全自动。
 *
 * 两步确认流程(§7.4.1):先「预览」(不写入台账)再「确认导入」(才真正写入)。
 * 目的是防止目标账号选错——如果选错的账号恰好与 CSV 真实所属账号有邮箱重叠
 * (如同一个人在多个测试账号里用同一个测试邮箱),旧版"上传即写入"会把不相关
 * 账号的记录静默覆盖掉,且不会报错、审计记录看起来也完全正常,运营事后很难
 * 察觉。预览步骤让管理员在真正写入前,先看一眼"这次要更新的记录是否符合预期"。
 * 预览期间锁定目标账号和文件选择,避免"预览的是 A、确认时手滑改成了 B"。
 */
export function ImportSubscriptionCsvPage() {
  const accounts = useAsync(() => listAccountConfigs(), []);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [result, setResult] = useState<ImportSubscriptionCsvResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const accountId = selectedAccount || accounts.data?.accounts[0]?.account_id || "";
  // 预览结果展示期间锁定账号/文件选择,防止确认导入时用的输入和预览时不一致
  const locked = result !== null && result.dry_run;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setResult(null);
    setError(null);
    if (!file) {
      setFileName(null);
      setCsvContent(null);
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setCsvContent(typeof reader.result === "string" ? reader.result : null);
    };
    reader.onerror = () => {
      setError("读取文件失败,请重试");
      setCsvContent(null);
    };
    reader.readAsText(file, "utf-8");
  };

  const handlePreview = async () => {
    if (!accountId) {
      setError("请先选择目标账号");
      return;
    }
    if (!csvContent) {
      setError("请先选择要上传的 CSV 文件");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await importSubscriptionCsv(accountId, csvContent, false);
      setResult(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "预览失败");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!accountId || !csvContent) return;
    setConfirming(true);
    setError(null);
    try {
      const resp = await importSubscriptionCsv(accountId, csvContent, true);
      setResult(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "确认导入失败");
    } finally {
      setConfirming(false);
    }
  };

  const resetFile = () => {
    setFileName(null);
    setCsvContent(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div>
      <h2 className="section-title">导入订阅状态(CSV 核对)</h2>

      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
          Kiro 官方未开放订阅状态查询 API,台账的"订阅状态"字段无法自动获知何时真正
          变为 Active。请到 Kiro 控制台「Users & Groups」页面,在 Subscriptions 表格
          点击「Download CSV」下载最新的订阅状态文件,再在这里选择对应的目标账号并
          上传该文件,系统会按邮箱自动比对并修正台账里的订阅状态,同时记录审计日志。
          此操作不会调用 IdC/Kiro 的任何写接口,只更新平台自己的台账数据。
          如果 CSV 里额外包含一列"到期日"(格式 YYYY-MM-DD),会同时用它同步更新
          用户列表里的到期日,不携带该列不影响原有导入行为。
        </p>
      </div>

      <div className="card">
        <div className="form-row">
          <label>目标账号</label>
          <select
            value={accountId}
            onChange={(e) => setSelectedAccount(e.target.value)}
            disabled={locked}
          >
            {accounts.data?.accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_id}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label>订阅状态 CSV 文件</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={locked}
          />
        </div>
        {fileName && (
          <p style={{ fontSize: 13, color: "#64748b" }}>
            已选择文件: {fileName}
            <button style={{ marginLeft: 8, fontSize: 11, padding: "2px 6px" }} onClick={resetFile}>
              {locked ? "重新选择(放弃本次预览)" : "清除"}
            </button>
          </p>
        )}
        {!locked && (
          <button className="primary" onClick={handlePreview} disabled={loading || !csvContent}>
            {loading ? "预览中..." : "预览导入结果"}
          </button>
        )}
        {!locked && (
          <p style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            点击「预览导入结果」不会修改任何台账数据,只是先算出这次导入会更新哪些记录,
            供你核对目标账号是否选对了,确认无误后再点「确认导入」才会真正写入。
          </p>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {result && (
        <div className="card" style={{ marginTop: 16 }}>
          {result.dry_run ? (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: 6,
                padding: 12,
                marginBottom: 16,
              }}
            >
              <strong>这是预览结果,尚未写入任何数据。</strong>
              请核对下方"已更新的记录"列表里的邮箱是否属于账号{" "}
              <code>{result.account_id}</code>,确认目标账号选对了、记录范围符合预期后,
              再点击下方「确认导入」按钮真正写入台账。
              <div style={{ marginTop: 10 }}>
                <button className="primary" onClick={handleConfirm} disabled={confirming}>
                  {confirming ? "写入中..." : "确认导入(将真正修改台账数据)"}
                </button>
                <button style={{ marginLeft: 8 }} onClick={resetFile} disabled={confirming}>
                  取消,重新选择
                </button>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "#dcfce7",
                border: "1px solid #22c55e",
                borderRadius: 6,
                padding: 12,
                marginBottom: 16,
              }}
            >
              ✅ 已确认写入,台账数据已更新完成,以下是最终结果。
            </div>
          )}
          <h3 style={{ marginTop: 0 }}>{result.dry_run ? "预览结果" : "导入结果"}</h3>
          <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, color: "#64748b" }}>CSV 总行数</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{result.total_rows}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{result.dry_run ? "将更新" : "已更新"}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#059669" }}>{result.updated_count}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#64748b" }}>无变化</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{result.unchanged_count}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#64748b" }}>仅 CSV 有(需核实)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#d97706" }}>{result.csv_only.length}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#64748b" }}>仅台账有(需核实)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#d97706" }}>{result.ledger_only.length}</div>
            </div>
            {result.skipped.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}>跳过(状态未识别)</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#dc2626" }}>{result.skipped.length}</div>
              </div>
            )}
          </div>

          {result.updated.length > 0 && (
            <>
              <h4>{result.dry_run ? "将会更新的记录(确认后才真正写入)" : "已更新的记录"}</h4>
              <p style={{ fontSize: 12, color: "#64748b" }}>
                若上传的 CSV 携带"到期日"列,会同时同步到期日到用户列表(见下方"到期日"列),
                不携带该列时行为不受影响。
              </p>
              <table>
                <thead>
                  <tr>
                    <th>邮箱</th>
                    <th>原状态</th>
                    <th>新状态</th>
                    <th>到期日</th>
                  </tr>
                </thead>
                <tbody>
                  {result.updated.map((u) => (
                    <tr key={u.email}>
                      <td>{u.email}</td>
                      <td>{u.before_status}</td>
                      <td>{u.after_status}</td>
                      <td>
                        {u.after_expiry ? (
                          <>
                            {u.before_expiry ?? "-"} → {u.after_expiry}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.csv_only.length > 0 && (
            <>
              <h4>仅 CSV 有,台账找不到对应记录</h4>
              <p style={{ fontSize: 12, color: "#64748b" }}>
                可能是绕开平台建号流程、直接在 Kiro/IdC 控制台操作产生的用户,系统不会自动补建台账记录,请人工核实。
              </p>
              <table>
                <thead>
                  <tr>
                    <th>邮箱</th>
                    <th>CSV 订阅状态</th>
                  </tr>
                </thead>
                <tbody>
                  {result.csv_only.map((r) => (
                    <tr key={r.email}>
                      <td>{r.email}</td>
                      <td>{r.csv_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.ledger_only.length > 0 && (
            <>
              <h4>仅台账有(非已取消),CSV 里没有出现</h4>
              <p style={{ fontSize: 12, color: "#64748b" }}>
                可能是刚建号还没被 Kiro 订阅报表收录,或者已经从 Kiro/IdC 完全移除,请人工核实。
              </p>
              <table>
                <thead>
                  <tr>
                    <th>邮箱</th>
                    <th>台账订阅状态</th>
                  </tr>
                </thead>
                <tbody>
                  {result.ledger_only.map((r) => (
                    <tr key={r.email}>
                      <td>{r.email}</td>
                      <td>{r.ledger_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.skipped.length > 0 && (
            <>
              <h4>已跳过的行(订阅状态取值无法识别)</h4>
              <table>
                <thead>
                  <tr>
                    <th>邮箱</th>
                    <th>原始状态值</th>
                    <th>原因</th>
                  </tr>
                </thead>
                <tbody>
                  {result.skipped.map((s, idx) => (
                    <tr key={`${s.email}-${idx}`}>
                      <td>{s.email}</td>
                      <td>{s.raw_status}</td>
                      <td>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.updated.length === 0 &&
            result.csv_only.length === 0 &&
            result.ledger_only.length === 0 &&
            result.skipped.length === 0 && (
              <p style={{ color: "#64748b" }}>
                {result.dry_run
                  ? "本次核对全部一致,确认导入也不会产生任何变化(可以直接确认或直接取消)。"
                  : "本次核对全部一致,台账数据无需更新。"}
              </p>
            )}
        </div>
      )}
    </div>
  );
}
